/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useCallback, useState, useEffect, useMemo,
} from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import { updatePathWithTabs } from "../../utils/Routes";
import BatchReports from "../Components/BatchReports/BatchReports";
import BatchReportPesticide from "../Components/BatchReports/Pesticides/BatchReportPesticide";
import { getUser } from "../../utils/helpers";

const AntTabs = withStyles({
  root: {
    backgroundColor: "#F4F5F7",
    borderBottom: "1px solid #e8e8e8",
  },
  indicator: {
    backgroundColor: "#494B83",
  },
})(Tabs);

const AntTab = withStyles((theme) => ({
  root: {
    textTransform: "none",
    minWidth: 160,
    fontWeight: theme.typography.fontWeightRegular,
    marginRight: theme.spacing(4),
    fontSize: "14px",
    lineHeight: "24px",
    color: "#6B778C",
    fontFamily: ["Roboto Bold"].join(","),
    "&:hover": {
      color: "#494B83",
      opacity: 1,
    },
    "&$selected": {
      color: "#494B83",
      fontWeight: theme.typography.fontWeightMedium,
    },
    "&:focus": {
      color: "#494B83",
    },
  },
  selected: {},
}))((props) => <Tab disableRipple {...props} />);

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  padding: {
    padding: theme.spacing(3),
  },
  chem: {
    backgroundColor: "#F4F5F7",
  },
}));

function TabPanel(props) {
  const {
    children, value, index, ...other
  } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`biochem-tabpanel-${index}`}
      aria-labelledby={`biochem-tab-${index}`}
      {...other}
    >
      {value === index && <div>{children}</div>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

TabPanel.defaultProps = {
  children: null,
};

function a11yProps(index) {
  return {
    id: `biochem-tab-${index}`,
    "aria-controls": `biochem-tabpanel-${index}`,
  };
}

export default function BatchReportTab() {
  const classes = useStyles();
  const [value, setValue] = useState(0);
  const history = useHistory();
  const page = "batchreports";
  const index = undefined;
  const currentPath = undefined;
  const user = useMemo(() => getUser(), []);
  const batchReportsViewOptions = useMemo(() => [
    { value: "ExcludeShelfLifeThirdParty", label: "Regular", permissions: ["view_batchreports_workorders", "view_batchreports_special_workorders"] },
    { value: "Shelflife", label: "Shelflife", permissions: ["view_batchreports_special_workorders"] },
    { value: "Thirdparty", label: "Third Party", permissions: ["view_batchreports_special_workorders"] },
  ], []);
  const authorizedViewOptions = useMemo(() => batchReportsViewOptions.filter(({ permissions }) => permissions.every((codename) => user.permissionMap[codename])), [batchReportsViewOptions, user.permissionMap]);
  const tabs = useMemo(() => [
    {
      tabLabel: "Workorders", route: "workorders", permissions: ["view_batchreports_workorders", "view_batchreports_special_workorders"], component: <BatchReports authorizedViewOptions={authorizedViewOptions} />,
    },
    {
      tabLabel: "MRL", route: "mrl", permissions: ["view_pesticides"], component: <BatchReportPesticide />,
    },
  ], [authorizedViewOptions]);
  const authorizedTabs = useMemo(() => tabs.filter((item) => item.permissions.some((codename) => user.permissionMap[codename])).map((item, i) => ({ ...item, tabValue: i })), [tabs, user]);
  const handleChange = useCallback((event, newValue) => {
    // Depending on the value of the tab, update the path
    updatePathWithTabs(currentPath, newValue, page, setValue, authorizedTabs, history);
    // Update the state
    setValue(newValue);
  }, [history, currentPath, authorizedTabs]);

  useEffect(() => {
    const path = window.location.pathname;
    // On refresh, makes sure the state is set correctly
    updatePathWithTabs(path, index, page, setValue, authorizedTabs, history);

    // Handles the case when the back button on the browser is clicked
    const unlisten = history.listen((location) => {
      updatePathWithTabs(location.pathname, index, page, setValue, authorizedTabs, history);
    });

    // Cleanup subscription on component unmount
    return () => {
      unlisten();
    };
  }, []); // eslint-disable-line

  return (
    <div className={classes.root}>
      <div className={classes.batchreports}>
        <AntTabs value={value} onChange={handleChange} aria-label="ant tabs">
          {authorizedTabs.map((item) => (<AntTab key={item.tabValue} label={item.tabLabel} {...a11yProps(item.tabValue)} />))}
        </AntTabs>
        {authorizedTabs.map((item) => (
          <TabPanel key={item.tabValue} value={value} index={item.tabValue}>
            {item.component}
          </TabPanel>
        ))}
      </div>
    </div>
  );
}
